import { useEffect, useState } from 'react';
import { useStreamChat } from '../contexts/StreamContext';

export interface RawPresence {
  /** Stream's raw "has an active WS connection" flag — generous; persists ~60s after disconnect. */
  streamOnline: boolean;
  /** ISO timestamp of last user activity per Stream. */
  lastActive: string | null;
}

export interface UserPresence extends RawPresence {
  /** Tightened: true only when streamOnline AND lastActive is fresh. */
  isOnline: boolean;
}

const ACTIVE_WINDOW_MS = 5 * 60 * 1000;

function tighten(raw: RawPresence): UserPresence {
  if (!raw.streamOnline) return { ...raw, isOnline: false };
  if (!raw.lastActive) return { ...raw, isOnline: true };
  const fresh = Date.now() - new Date(raw.lastActive).getTime() < ACTIVE_WINDOW_MS;
  return { ...raw, isOnline: fresh };
}

/**
 * Subscribe to Stream Chat presence for a single user.
 * Stream only delivers user.presence.changed events for users that have been
 * queried with presence:true, so we issue the query on mount and listen
 * for events until unmount.
 */
export function useUserPresence(userId: string | null | undefined): UserPresence {
  const { client } = useStreamChat();
  const [raw, setRaw] = useState<RawPresence>({ streamOnline: false, lastActive: null });

  useEffect(() => {
    if (!client || !userId) {
      setRaw({ streamOnline: false, lastActive: null });
      return;
    }
    let cancelled = false;

    client
      .queryUsers({ id: { $in: [userId] } }, {}, { presence: true })
      .then(result => {
        if (cancelled) return;
        const user = result.users.find(u => u.id === userId);
        if (user) {
          setRaw({
            streamOnline: Boolean(user.online),
            lastActive: (user.last_active as string | undefined) ?? null,
          });
        }
      })
      .catch(() => {});

    const handler = client.on('user.presence.changed', (event: { user?: { id?: string; online?: boolean; last_active?: string } }) => {
      if (cancelled) return;
      const u = event.user;
      if (!u || u.id !== userId) return;
      setRaw({
        streamOnline: Boolean(u.online),
        lastActive: u.last_active ?? null,
      });
    });

    return () => {
      cancelled = true;
      handler.unsubscribe();
    };
  }, [client, userId]);

  return tighten(raw);
}

/** "Active now" / "Active 12m ago" / null. */
export function formatPresence(presence: UserPresence): string | null {
  if (presence.isOnline) return 'Active now';
  if (!presence.lastActive) return null;
  const diff = Date.now() - new Date(presence.lastActive).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Active just now';
  if (mins < 60) return `Active ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Active ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Active ${days}d ago`;
  return null;
}
